import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import Pager from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { KeyRound, Copy, Shield, User } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const PAGE_SIZE = 20;

export default function Admins() {
  const [admins, setAdmins] = useState([]);
  const [reset, setReset] = useState(null); // { reset_url, expires_at, email }
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async (p) => {
    try {
      const { data } = await api.get("/admin/admins", { params: { page: p, page_size: PAGE_SIZE } });
      setAdmins(data.items); setTotal(data.total);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(page); }, [page]);

  const issueReset = async (id) => {
    try {
      const { data } = await api.post(`/admin/admins/${id}/reset-password`);
      setReset(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div>
      <PageHeader
        testId="admins-header"
        title="Administrators"
        subtitle="Manage admin accounts and issue password reset links"
      />
      <div className="p-4 md:p-8">
        <div className="border border-nhp-border bg-nhp-surface divide-y divide-nhp-border">
          {admins.length === 0 && <div className="p-10 text-center text-sm text-nhp-muted">No admins yet.</div>}
          {admins.map((a) => (
            <div key={a.id} data-testid={`admin-row-${a.id}`} className="px-4 md:px-5 py-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 shrink-0 grid place-items-center bg-nhp-bg border border-nhp-border">
                  {a.role === "super_admin" ? <Shield className="h-4 w-4 text-nhp-primary" /> : <User className="h-4 w-4 text-nhp-muted" />}
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate">{a.name}</div>
                  <div className="font-mono text-xs text-nhp-muted truncate">{a.email}</div>
                </div>
              </div>
              <div className="flex items-center justify-between md:justify-end gap-4">
                <div className="md:text-right">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">{a.role}</div>
                  <div className="font-mono text-[10px] text-nhp-muted mt-1">
                    {a.last_login_at ? `Last login ${new Date(a.last_login_at).toLocaleDateString()}` : "Never signed in"}
                  </div>
                </div>
                <Button
                  data-testid={`reset-admin-${a.id}`}
                  onClick={() => issueReset(a.id)}
                  variant="ghost"
                  className="h-9 rounded-none border border-nhp-border hover:border-nhp-primary hover:text-nhp-primary shrink-0"
                >
                  <KeyRound className="h-4 w-4 md:mr-1" /> <span className="hidden md:inline">RESET PASSWORD</span>
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="border-x border-b border-nhp-border bg-nhp-surface">
          <Pager page={page} total={total} pageSize={PAGE_SIZE} itemLabel="admins" onChange={setPage} testId="admins" />
        </div>
      </div>

      <Dialog open={!!reset} onOpenChange={(v) => !v && setReset(null)}>
        <DialogContent className="bg-nhp-surface border-nhp-border text-nhp-text rounded-none max-w-lg" data-testid="reset-link-dialog">
          <DialogHeader>
            <DialogTitle>Password reset link ready</DialogTitle>
            <DialogDescription className="text-nhp-muted">
              Send this URL to {reset?.email}. It expires {reset?.expires_at && new Date(reset.expires_at).toLocaleString()}.
            </DialogDescription>
          </DialogHeader>
          <div className="border border-nhp-border bg-nhp-bg p-3 font-mono text-xs break-all" data-testid="reset-link-url">
            {reset?.reset_url}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReset(null)} className="rounded-none">Close</Button>
            <Button
              data-testid="copy-reset-link-btn"
              onClick={() => { navigator.clipboard.writeText(reset.reset_url); toast.success("Link copied"); }}
              className="rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold"
            >
              <Copy className="h-4 w-4 mr-2" /> COPY LINK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
